import { Check, RotateCcw, X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import { useStickToBottom } from "use-stick-to-bottom";

import { AgentMarkdown } from "../chat/AgentMarkdown";
import { cn } from "../lib/utils";
import { Button } from "../ui/button";
import {
  CommentComposer,
  type CommentAgent,
  type CommentPost,
} from "./CommentComposer";

/** The card's width in px. Agents answer at length, so it runs wider than a
 *  typical comment card; hosts placing it read this to keep it in view. */
export const COMMENT_CARD_WIDTH = 400;

export interface ThreadMessage {
  id: string;
  authorName: string;
  /** Posted by an agent rather than a person. */
  agent: boolean;
  body: string;
  createdAt: number;
}

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

function postedAgo(createdAt: number): string {
  const minutes = Math.round((createdAt - Date.now()) / 60_000);
  if (Math.abs(minutes) < 1) return "just now";
  if (Math.abs(minutes) < 60) return relative.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return relative.format(hours, "hour");
  return new Date(createdAt).toLocaleDateString();
}

function Initials({ name, agent }: { name: string; agent: boolean }) {
  return (
    <span
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
        agent
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground",
      )}
    >
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

/**
 * One comment thread, shown where it is anchored: the quoted excerpt, the
 * messages from people and agents in order, and a reply field. A thread with
 * no messages yet is a draft, and has nothing to resolve.
 */
export function CommentThread({
  excerpt,
  outdated,
  messages,
  resolved,
  agent,
  onResolvedChange,
  onPost,
  onClose,
  followsSelection = false,
}: {
  /** The text the thread is anchored to, when it has one. */
  excerpt?: ReactNode;
  /** Why the anchor no longer matches what's there now. */
  outdated?: string;
  messages: ThreadMessage[];
  resolved: boolean;
  agent?: CommentAgent;
  onResolvedChange?(resolved: boolean): void;
  onPost(post: CommentPost): void;
  onClose(): void;
  /**
   * The host shows the card while its anchor is selected (a mark on a
   * canvas), so the host's own clicks open and close it: a click outside is
   * the host's to read (a resize handle, another mark), and the composer
   * waits to be clicked rather than taking the host's keys.
   */
  followsSelection?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const onDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    window.addEventListener("keydown", onKey, true);
    if (!followsSelection) {
      window.addEventListener("pointerdown", onDown, true);
    }
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onDown, true);
    };
  }, [onClose, followsSelection]);

  // Opens on the newest message and follows replies, as the chat does,
  // unless the reader has scrolled up. Posting brings them back down.
  const { scrollRef, contentRef, scrollToBottom } = useStickToBottom({
    initial: "instant",
    resize: "smooth",
  });

  return (
    <div
      ref={ref}
      style={{ width: COMMENT_CARD_WIDTH }}
      className="flex max-w-[calc(100vw-1rem)] flex-col gap-2.5 rounded-lg border bg-popover p-3 font-sans text-popover-foreground shadow-lg"
      // Typing here is the thread's, not the host editor's shortcuts.
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {excerpt ? (
            <p className="line-clamp-2 border-l-2 border-amber-400 pl-2 text-xs text-muted-foreground">
              {excerpt}
            </p>
          ) : null}
          {outdated ? (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
              {outdated}
            </p>
          ) : null}
        </div>
        {onResolvedChange && messages.length > 0 ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label={resolved ? "Reopen" : "Resolve"}
            title={resolved ? "Reopen" : "Resolve"}
            onClick={() => onResolvedChange(!resolved)}
          >
            {resolved ? (
              <RotateCcw className="size-3.5" />
            ) : (
              <Check className="size-3.5" />
            )}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          aria-label="Close"
          onClick={onClose}
        >
          <X className="size-3.5" />
        </Button>
      </div>
      {resolved ? (
        <p className="text-xs text-muted-foreground">Resolved</p>
      ) : null}
      {messages.length > 0 ? (
        <div ref={scrollRef} className="max-h-72 overflow-y-auto">
          <ol ref={contentRef} className="flex flex-col gap-3">
            {messages.map((message) => (
              <li key={message.id} className="flex gap-2">
                <Initials name={message.authorName} agent={message.agent} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5 text-xs">
                    <span className="font-medium">{message.authorName}</span>
                    <span className="text-muted-foreground">
                      {postedAgo(message.createdAt)}
                    </span>
                  </div>
                  {/* The same markdown as the chat: an agent's answer has lists
                    and emphasis, and a person's line breaks should survive. */}
                  <AgentMarkdown className="prose prose-sm mt-0.5 max-w-none break-words text-sm dark:prose-invert prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                    {message.body}
                  </AgentMarkdown>
                </div>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
      <CommentComposer
        placeholder={messages.length > 0 ? "Reply" : "Add a comment"}
        agent={agent}
        autoFocus={!followsSelection}
        onPost={(post) => {
          onPost(post);
          void scrollToBottom();
        }}
      />
    </div>
  );
}
